import React, { useEffect, useState } from 'react';
import { Camera, CheckCircle, AlertCircle, UserRound, Upload } from 'lucide-react';

export default function PublicPatientPhotoPage() {
  const token = window.location.pathname.split('/').filter(Boolean).pop();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8001'
      : '');

  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const isMobileOrTablet =
    typeof window !== 'undefined' &&
    /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(window.navigator.userAgent);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/patients/photo-public/${token}`);

        if (!response.ok) {
          throw new Error('Link inválido ou expirado.');
        }

        const data = await response.json();
        setInfo(data);
      } catch (err) {
        setError(err.message || 'Não foi possível carregar o link.');
      } finally {
        setLoading(false);
      }
    };

    if (token) loadInfo();
  }, [backendUrl, token]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];

    if (!selected) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type)) {
      setError('Envie uma imagem JPG, PNG ou WEBP.');
      return;
    }

    if (selected.size > 8 * 1024 * 1024) {
    setError('A foto deve ter no máximo 8MB.');
    return;
    }

    setError('');
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError('Selecione ou tire uma foto antes de enviar.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/patients/photo-public/${token}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erro ao enviar foto.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Erro ao enviar foto.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link indisponível</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-6 text-center">
          <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Foto enviada com sucesso</h1>
          <p className="text-gray-600">
            Obrigado. A clínica já poderá visualizar sua foto no prontuário.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <h1 className="text-2xl font-bold">Envio de foto do paciente</h1>
          <p className="text-blue-100 mt-2">
            {info?.clinic_name || 'Clínica odontológica'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <div className="text-sm text-gray-500">Paciente</div>
            <div className="text-lg font-semibold text-gray-900">
              {info?.patient_name || 'Paciente'}
            </div>
          </div>

          <div className="flex justify-center">
            {preview ? (
              <img
                src={preview}
                alt="Pré-visualização da foto"
                className="w-36 h-36 rounded-2xl object-cover border border-gray-200 shadow-sm"
              />
            ) : (
              <div className="w-36 h-36 rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                <UserRound className="w-14 h-14 text-gray-400" />
              </div>
            )}
          </div>

          <div className="space-y-3">
          <div className="block text-sm font-medium text-gray-700">
              Foto do paciente
          </div>

          {isMobileOrTablet ? (
              <div className="grid grid-cols-1 gap-3">
              <label className="w-full inline-flex items-center justify-center bg-blue-600 text-white rounded-xl px-4 py-3 font-semibold hover:bg-blue-700 cursor-pointer">
                  <Camera className="w-5 h-5 mr-2" />
                  Tirar foto pela câmera
                  <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={handleFileChange}
                  className="hidden"
                  />
              </label>
  
              <label className="w-full inline-flex items-center justify-center border border-gray-300 text-gray-700 rounded-xl px-4 py-3 font-semibold hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-5 h-5 mr-2" />
                  Escolher foto do celular
                  <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  />
              </label>
              </div>
          ) : (
              <label className="w-full inline-flex items-center justify-center border border-gray-300 text-gray-700 rounded-xl px-4 py-3 font-semibold hover:bg-gray-50 cursor-pointer">
              <Upload className="w-5 h-5 mr-2" />
              Escolher arquivo
              <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
              />
              </label>
          )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          <p className="text-xs text-gray-500">
            Ao enviar, você autoriza o uso desta imagem apenas para identificação no prontuário da clínica.
          </p>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center bg-blue-600 text-white rounded-xl px-4 py-3 font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            <Camera className="w-5 h-5 mr-2" />
            {submitting ? 'Enviando...' : 'Enviar foto'}
          </button>
        </form>
      </div>
    </div>
  );
}